<template>
  <div class="dashboard-planning-page">
    <DashboardDetailDrawer />

    <QPageContainer>
      <QPage padding>
        <div class="row">
          <div class="col-12 col-md">
            <PlanningCalendar v-model:date-range="selectedDateRange" :sticky="$q.screen.gt.sm" :shifts="calendarShifts" />
          </div>
          <div class="col-12 col-md">
            <QBtn
              :label="$t('ETAB.PAGES.PLANNING.@SEE_PREVIOUS_SHIFTS', { number: 7 })"
              class="full-width"
              flat
              color="accent"
              no-caps
              @click="loadPreviousShifts()"
            />
            <PlanningShiftsList :shifts-list="planningShifts" />
            <QBtn
              :label="$t('ETAB.PAGES.PLANNING.@SEE_NEXT_SHIFTS', { number: 7 })"
              class="full-width"
              flat
              color="accent"
              no-caps
              @click="loadNextShifts()"
            />
          </div>
        </div>

        <QPageScroller position="bottom-right" :scroll-offset="150" :offset="[10, 8]">
          <QBtn fab icon="keyboard_arrow_up" color="accent" />
        </QPageScroller>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script>
  import { CalendarShiftsSummaryListModel, PlanningShiftModel, ICalendarShiftsSummaryList, PlanningShiftsGroupModel } from "@core/models";
  import { DatesHelpers, PlanningHelpers } from "@core/helpers";
  import { EventManager } from "@core/managers/event.manager";
  import { ETAB_EVENTS, ETAB_ROUTES } from "@etab/config";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { NativeCommunicationManager } from "@core/managers/native-communication.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { ShiftsService } from "@core/services";
  import { GETTER_EXTRA_GET_CURRENT } from "@etab/stores/types";
  import { useExtraStore } from "@etab/stores/extra";
  import { mapState } from "pinia";

  import DashboardDetailDrawer from "@etab/layouts/dashboard/DashboardDetailDrawer.component.vue";
  import PlanningCalendar from "@etab/components/planning/PlanningCalendar.component.vue";
  import PlanningShiftsList from "@etab/components/planning/PlanningShiftsList.component.vue";

  export default {
    components: {
      DashboardDetailDrawer,
      PlanningCalendar,
      PlanningShiftsList,
    },
    data() {
      return {
        calendarShifts: new CalendarShiftsSummaryListModel(),
        planningShifts: [],
        selectedDateRange: {},
      };
    },
    computed: {
      ...mapState(useExtraStore, {
        extra: GETTER_EXTRA_GET_CURRENT,
      }),
    },
    async created() {
      this.events = [
        { name: ETAB_EVENTS.SHIFT_PLANNING.GET_CALENDAR_FROM_DATE, action: this.getCalendarShifts },
        { name: ETAB_EVENTS.SHIFT_PLANNING.GET_SHIFTS_FROM_DATES, action: this.getPlanningShifts },
        { name: ETAB_EVENTS.SHIFT_PLANNING.REFRESH, action: this.refresh },
        { name: ETAB_EVENTS.SHIFT_PLANNING.OPEN_SHIFT, action: this.navigateToMissionDetail },
        { name: ETAB_EVENTS.SHIFT_PLANNING.OPEN_PHONE, action: this.openPhone },
      ];

      this.registerEvents();
      this.initializeData();
    },
    beforeUnmount() {
      this.unregisterEvents();
    },

    methods: {
      registerEvents() {
        EventManager.registerAllEvents(this.events);
      },
      unregisterEvents() {
        EventManager.unregisterAllEvents(this.events);
      },
      async initializeData() {
        const { start, end } = PlanningHelpers.getFirstWeekSelection();
        this.selectedDateRange = { start, end };
        return this.getPlanningShifts({ start, end });
      },
      async refresh() {
        const { start, end } = this.selectedDateRange;
        return this.getPlanningShifts({ start, end });
      },
      async loadNextShifts() {
        const { start, end } = DatesHelpers.getNextWeekFromDate(this.selectedDateRange.end);
        this.selectedDateRange = { start, end };
        await this.getPlanningShifts({ start, end });
        ScrollManager.scrollToTop({ withDelay: true });
      },
      async loadPreviousShifts() {
        const { start, end } = DatesHelpers.getPreviousWeekFromDate(this.selectedDateRange.start);
        this.selectedDateRange = { start, end };
        await this.getPlanningShifts({ start, end });
      },
      async getPlanningShifts({ start, end } = {}) {
        try {
          this.$_startGlobalLoading();
          const [planningShifts] = await Promise.all([
            ShiftsService.getCalendarShiftsDetail({ start, end }),
            // ensure calendar is updated as well
            this.getCalendarShifts({ date: start, displayLoading: false }),
          ]);
          this.planningShifts = planningShifts.map((group) => new PlanningShiftsGroupModel(group));
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async getCalendarShifts({ date, displayLoading = true } = {}) {
        try {
          if (displayLoading) {
            this.$_startGlobalLoading();
          }
          // regarding the size of the display, we take the next 0 or 1 month + 2 weeks
          // in order to match with the calendar display of 6 weeks/rows
          const additionnalMonth = this.$q.screen.gt.sm ? 1 : 0;
          const { start, end } = PlanningHelpers.getCalendarShiftsBoundaries({ date, additionnalMonth });
          const calendarShifts = await ShiftsService.getCalendarSummary({ start, end });
          this.calendarShifts = new CalendarShiftsSummaryListModel({ [ICalendarShiftsSummaryList.shifts]: calendarShifts });
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        } finally {
          if (displayLoading) {
            this.$_stopGlobalLoading();
          }
        }
      },
      async navigateToMissionDetail(shift = new PlanningShiftModel()) {
        // prevent double navigation through double click
        if (!this.$_isGlobalLoadingActive) {
          const missionId = shift.getMissionId();
          const extraId = shift.getExtraBooked().getId();
          this.$router.push({ name: ETAB_ROUTES.dashboard.planning.mission.name, params: { missionId, extraId } }).catch((suppressedError) => {});
        }
      },
      openPhone(shift = new PlanningShiftModel()) {
        const phone = shift.getExtraBooked().getIdentity().phone;
        if (phone) {
          NativeCommunicationManager.phone({ phone });
        }
      },
    },
  };
</script>
